import { toast } from 'vue-sonner'
import { captureError } from '../lib/sentry'

/**
 * App-wide toast feedback, rendered by the <Toaster /> outlet in App.vue.
 *
 * Backed by the vendored vue-sonner package (packages/vue-sonner), whose
 * `toast` is a module-level singleton — so this also works from Pinia stores
 * and composables outside a component's setup context.
 */

function show(kind: 'success' | 'error' | 'info', title: string, message: string, life: number) {
  const fn = kind === 'success' ? toast.success : kind === 'error' ? toast.error : toast.info
  fn(title, { description: message, duration: life })
}

export function useToast() {
  const success = (message: string) => {
    console.log('Success:', message)
    show('success', 'Gelukt', message, 4000)
  }

  const error = (message: string, cause?: unknown, options?: { report?: boolean }) => {
    console.error('Error:', message, cause ?? '')
    // Surface user-facing errors to Sentry. Many stores catch and swallow the
    // original error here, so this is the funnel that gets it to monitoring.
    // Pass the original error as `cause` wherever possible so Sentry receives
    // the real exception instead of a synthetic one. Pass `{ report: false }`
    // for pure client-side validation feedback (e.g. "file too large") that
    // is not an application defect.
    if (options?.report !== false) {
      captureError(cause ?? new Error(message), { toastMessage: message })
    }
    show('error', 'Fout', message, 8000)
  }

  const info = (message: string) => {
    console.info('Info:', message)
    show('info', 'Info', message, 4000)
  }

  return {
    success,
    error,
    info
  }
}
